import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, BookOpen, Calculator, Mail, Compass } from 'lucide-react';

import { SUPPORTED_LANGS, DEFAULT_LANG } from '@/i18n';
import { pathFor } from '@/data/routes';

/**
 * Bottom navigation bar — mobile only. Sticky at the bottom of the viewport.
 * 5 anchors: Home, Start Here, Blog, Tools, Newsletter. Active state via
 * pathname matching. Hidden when keyboard is up (iOS keyboard pushes content
 * — `data-hidden-when-keyboard` for future enhancement).
 *
 * @example
 *   <BottomNavMobile />  // mounted globally in <Layout>
 */
export default function BottomNavMobile() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const lang = SUPPORTED_LANGS.includes(i18n.language) ? i18n.language : DEFAULT_LANG;

  // Resolve the current path's "section" by matching prefix.
  const isActive = (route) => {
    const target = pathFor(route, lang);
    if (target === pathFor('home', lang)) {
      // Home is exact-match only (otherwise everything starts with /).
      return location.pathname === target || location.pathname === '/';
    }
    return location.pathname.startsWith(target);
  };

  const items = [
    { route: 'home', icon: Home, label: t('common:home') },
    { route: 'startHere', icon: Compass, label: t('navigation:startHere') },
    { route: 'blog', icon: BookOpen, label: t('navigation:blogShort') },
    { route: 'tools', icon: Calculator, label: t('navigation:tools') },
    { route: 'newsletter', icon: Mail, label: t('navigation:newsletter') },
  ];

  return (
    <nav
      aria-label={t('navigation:primary')}
      className="fixed inset-x-0 bottom-0 z-30 border-t border-navy-900/10 bg-cream-50/95 backdrop-blur lg:hidden print-hide dark:border-white/10 dark:bg-black/95"
      data-print="hide"
    >
      <ul className="grid grid-cols-5">
        {items.map(({ route, icon: Icon, label }) => {
          const active = isActive(route);
          return (
            <li key={route} className="flex">
              <Link
                to={pathFor(route, lang)}
                aria-current={active ? 'page' : undefined}
                className={`flex flex-1 flex-col items-center justify-center gap-1 py-2.5 text-[10px] font-medium uppercase tracking-wide transition-colors ${
                  active
                    ? 'text-bordeaux-700 dark:text-bordeaux-400'
                    : 'text-gray-700 hover:text-navy-900 dark:text-white/60 dark:hover:text-white'
                }`}
              >
                <Icon size={18} aria-hidden="true" />
                <span className="truncate">{label}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
